import {
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogBackdrop,
} from '@headlessui/react';
import { useReactFlow } from '@xyflow/react';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from 'src/components/button';
import { CloseIon } from 'src/components/icons/CloseIcon';
import { DownloadFile } from 'src/components/icons/DownloadFile';
import { LoadingIcon } from 'src/components/icons/LoadingIcon';
import { defaultProjectData } from 'src/config';
import { useGlobalContext } from 'src/context/global/use-global-context';
import {
  exportData,
  ExportFileType,
  ExportDataType,
} from 'src/utils/export-data';
import { twMerge } from 'tailwind-merge';

export default function DialogDownloadFile() {
  const { setShowDialogs, vlans, projectData } = useGlobalContext();
  const { getNodes, getEdges } = useReactFlow();

  const [selectedFileType, setSelectedFileType] = useState<ExportFileType>(
    ExportFileType.EXCEL,
  );
  const [selectedDataType, setSelectedDataType] = useState<ExportDataType>(
    ExportDataType.SWITCHES,
  );
  const [isExporting, setIsExporting] = useState(false);

  const onCancel = () => {
    setShowDialogs({ downloadFile: false });
  };

  const handleExport = async () => {
    if (isExporting) return;

    setIsExporting(true);
    try {
      const nodes = getNodes();
      const edges = getEdges();

      exportData(
        selectedDataType,
        selectedFileType,
        nodes,
        edges,
        vlans,
        projectData || defaultProjectData,
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Export failed:', error);
      toast.error('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const fileTypeOptions = [
    {
      value: ExportFileType.EXCEL,
      label: 'MS Excel (.xlsx)',
      description: '',
    },
    {
      value: ExportFileType.LIBREOFFICE,
      label: 'LibreOffice Calc (.ods)',
      description: '',
    },
    {
      value: ExportFileType.CSV,
      label: 'CSV (.csv)',
      description: '',
    },
  ];

  const dataTypeOptions = [
    {
      value: ExportDataType.SWITCHES,
      label: 'Export List of Switches',
      description: '',
    },
    {
      value: ExportDataType.DEVICE_IP_VLAN,
      label: 'Export Device IP/VLAN List',
      description: '',
    },
    {
      value: ExportDataType.PORT_ASSIGNMENT,
      label: 'Export Port Assignment',
      description: '',
    },
  ];

  return (
    <Dialog
      open
      onClose={onCancel}
      className="relative z-50"
      data-testid="dialog-download-file"
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            data-testid="dialog-download-file-panel"
            className={twMerge([
              'relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6',
              'data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95',
            ])}
          >
            <DialogTitle
              as="h3"
              className="pb-4 text-xl font-bold text-gray-900"
              data-testid="dialog-download-file-title"
            >
              Download File
            </DialogTitle>
            <div className="absolute right-4 top-4">
              <button
                type="button"
                onClick={onCancel}
                data-testid="dialog-download-file-close-button"
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Close</span>
                <CloseIon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Data Type Selection */}
              <div>
                <label
                  className="text-base font-medium text-gray-900"
                  data-testid="dialog-download-file-data-type-label"
                >
                  Select data to export
                </label>
                <p className="text-sm leading-5 text-gray-500">
                  Choose the type of data you want to export
                </p>
                <fieldset className="mt-4">
                  <legend className="sr-only">Data type selection</legend>
                  <div className="space-y-4">
                    {dataTypeOptions.map((option) => (
                      <div key={option.value} className="flex items-start">
                        <div className="flex h-6 items-center">
                          <input
                            id={`data-type-${option.value}`}
                            name="data-type"
                            type="radio"
                            data-testid={`dialog-download-file-data-type-${option.value}`}
                            checked={selectedDataType === option.value}
                            onChange={() => setSelectedDataType(option.value)}
                            className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-600"
                          />
                        </div>
                        <label
                          htmlFor={`data-type-${option.value}`}
                          className="ml-3 text-sm leading-6"
                        >
                          <span className="font-medium text-gray-900">
                            {option.label}
                          </span>
                          <p className="text-gray-500">{option.description}</p>
                        </label>
                      </div>
                    ))}
                  </div>
                </fieldset>
              </div>

              {/* File Type Selection */}
              <div>
                <label
                  className="text-base font-medium text-gray-900"
                  data-testid="dialog-download-file-file-type-label"
                >
                  Select file format
                </label>
                <p className="text-sm leading-5 text-gray-500">
                  Choose the file format for export
                </p>
                <fieldset className="mt-4">
                  <legend className="sr-only">File format selection</legend>
                  <div className="space-y-4">
                    {fileTypeOptions.map((option) => (
                      <div key={option.value} className="flex items-start">
                        <div className="flex h-6 items-center">
                          <input
                            id={`file-type-${option.value}`}
                            name="file-type"
                            type="radio"
                            data-testid={`dialog-download-file-file-type-${option.value}`}
                            checked={selectedFileType === option.value}
                            onChange={() => setSelectedFileType(option.value)}
                            className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-600"
                          />
                        </div>
                        <label
                          htmlFor={`file-type-${option.value}`}
                          className="ml-3 text-sm leading-6"
                        >
                          <span className="font-medium text-gray-900">
                            {option.label}
                          </span>
                          <p className="text-gray-500">{option.description}</p>
                        </label>
                      </div>
                    ))}
                  </div>
                </fieldset>
              </div>
            </div>

            {/* Action buttons */}
            <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                color="gray"
                onClick={onCancel}
                disabled={isExporting}
                data-testid="dialog-download-file-cancel-button"
                className="flex items-center justify-center"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleExport}
                disabled={isExporting}
                data-testid="dialog-download-file-export-button"
                className="flex items-center justify-center"
              >
                {isExporting ? (
                  <LoadingIcon className="mr-2" />
                ) : (
                  <DownloadFile className="mr-2 h-4 w-4" />
                )}
                {isExporting ? 'Exporting...' : 'Export'}
              </Button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
